import { useEffect } from 'react';
import { SITE } from '@/data/site';

/** Canonical production origin — the .web.app mirror canonicals here too. */
const ORIGIN = 'https://gracehaven.co.bw';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/**
 * Lightweight per-route head manager: title, description, canonical, and
 * Open Graph / Twitter tags — so each page is properly indexable and shareable.
 */
export function Seo({ title, description, image }: { title: string; description?: string; image?: string }) {
  useEffect(() => {
    const brand = SITE.wordmark; // "Grace Haven"
    const fullTitle = title.includes(brand) ? title : `${title} · ${brand}`;
    const url = ORIGIN + window.location.pathname;
    const img = image || `${ORIGIN}/images/icon-512.png`;

    document.title = fullTitle;
    upsertCanonical(url);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', brand);
    upsertMeta('property', 'og:image', img);
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:image', img);

    if (description) {
      upsertMeta('name', 'description', description);
      upsertMeta('property', 'og:description', description);
      upsertMeta('name', 'twitter:description', description);
    }
  }, [title, description, image]);

  return null;
}
